import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { movies, getByCategory, MovieItem } from '@/mocks/movies';

const tabs = [
  { key: 'movie', label: '电影', icon: 'ri-film-line' },
  { key: 'tv', label: '电视剧', icon: 'ri-tv-line' },
  { key: 'variety', label: '综艺', icon: 'ri-live-line' },
  { key: 'anime', label: '动漫', icon: 'ri-rocket-line' },
];

const sortOptions = [
  { key: 'hot', label: '热门推荐' },
  { key: 'new', label: '最新更新' },
  { key: 'rating', label: '评分最高' },
];

function MovieCard({ item }: { item: MovieItem }) {
  return (
    <Link
      to={`/article/${item.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5 bg-background-200">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
            {item.rating}
          </span>
        </div>
        {item.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-accent-500/90 text-background-950 whitespace-nowrap">
              NEW
            </span>
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-xs text-foreground-50 bg-primary-500/90 px-3 py-1.5 rounded-full flex items-center justify-center gap-1 whitespace-nowrap">
            <i className="ri-play-fill text-xs"></i> 立即观看
          </span>
        </div>
      </div>
      <h4 className="text-sm font-medium text-foreground-50 line-clamp-1 group-hover:text-primary-400 transition-colors">
        {item.title}
      </h4>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-foreground-500">{item.year}</span>
        <span className="text-xs text-foreground-600">|</span>
        <span className="text-xs text-foreground-500">{item.genre[0]}</span>
      </div>
    </Link>
  );
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [sort, setSort] = useState('hot');

  const currentTab = tabs.find((t) => t.key === type) || tabs[0];

  const items = useMemo(() => {
    const categoryItems = getByCategory(type || 'movie');
    switch (sort) {
      case 'new':
        return [...categoryItems].sort((a, b) => b.year - a.year);
      case 'rating':
        return [...categoryItems].sort((a, b) => b.rating - a.rating);
      case 'hot':
      default:
        return [...categoryItems].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
    }
  }, [type, sort]);

  const categoryLabelMap: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };

  const categoryLabel = categoryLabelMap[type || 'movie'] || '电影';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `八戒影视 - ${categoryLabel}大全`,
    description: `八戒影视网${categoryLabel}频道，提供最新${categoryLabel}免费在线观看，高清画质秒速加载不缓冲。`,
    url: `https://www.bajieying.com/category/${type}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '八戒影视网',
      url: 'https://www.bajieying.com/',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bajieying.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      {/* Breadcrumb */}
      <div className="w-full px-4 md:px-6 pt-20 pb-0">
        <div className="max-w-6xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-300">{categoryLabel}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="w-full px-4 md:px-6 pb-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-heading text-foreground-50 font-bold mb-2">
            {categoryLabel}大全
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            八戒影视网为您精选最新{categoryLabel}资源，高清画质秒速加载，免费在线观看
          </p>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => navigate(`/category/${tab.key}`)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  currentTab.key === tab.key
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-50'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-foreground-500">排序：</span>
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSort(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sort === opt.key
                    ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-50 bg-background-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="w-full px-4 md:px-6 pb-14">
        <div className="max-w-6xl mx-auto">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {items.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}